import React, { useEffect, useMemo, useState } from "react";
import { Paper, Stack, Typography, Alert, Button } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../../components/Header';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import ImportX3UsersForm from "../../components/forms/ImportX3UsersForm";
import { getAll as getAllGroups } from '../../services/userGroupsService';
import { getX3Folders, getX3Users } from '../../services/x3Service';
import { 
  importX3Users as importX3Users, 
} from '../../services/userService';

function ImportUsersView(props) {
  const { parsed, userReducer } = props;

  const isAdmin = userReducer?.userType?.toLowerCase() === 'admin';

  // ----- data from APIs -----
  const [folders, setFolders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [x3Users, setX3Users] = useState([]);

  // ----- loading/errors -----
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");

  // ----- form state -----
  const [formState, setFormState] = useState({
    folder: "",
    setAllFolders: false,
    selectedGroups: [],
    selectedUsrSet: new Set(),
    userSearch: "",
  });

  const title = useMemo(() => {
    const localisation = parsed?.localisation || {};
    return localisation["menu_header_administration_import_users"]
      ? localisation["menu_header_administration_import_users"].element_lang
      : "Administration - Import Users";
  }, [parsed]);

  const labels = useMemo(() => {
    const loc = userReducer?.localisation ?? {};
    return {
      folderLabel: loc?.table_header_folder?.element_lang ?? "Folder",
      addUsersLabel: loc?.add_users_button?.element_lang ?? "Add users",
    };
  }, [userReducer]);

  const loadX3Folders = async () => {
    try {
      setLoadingInit(true);
      let x3Folders = [];
      const result  = await getX3Folders();
      if (!result.ok) {
          setError('Error loading x3 folders. Error status: ' + result.status + " OK: " + result.ok);
          return;
      }
      x3Folders = result.data;
      const folderNames = (x3Folders ?? [])
          .map((x) => (typeof x === "string" ? x : x.folder))
          .filter(Boolean);

          setFolders(folderNames);

          setFormState((prev) => {
            const next = { ...prev };
            if (!next.folder && folderNames.length) next.folder = folderNames[0];
            return next;
          });
    } catch (err) {
      setError(err.message || 'Failed to load x3 folders');
    }
    finally {
      setLoadingInit(false);
    }
  };

  const handleGoBackClick = () => {
    props.history.push('/site/administration/Users')
  }

  // Redirect non-admin users
  useEffect(() => {
    if (userReducer?.userType && !isAdmin) {
      setError('Access denied. Only administrators can import users.');
      const timer = setTimeout(() => {
        props.history.push('/site/administration/Users');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAdmin, userReducer?.userType, props.history]);

  // ---------- Load folders & groups once ----------
  useEffect(() => {
    const controller = new AbortController();

    async function loadInitial() {
      setError("");
      setSuccessMsg("");
      setWarningMsg("");
      setLoadingInit(true);

      try {
        const groupsRes = await getAllGroups();

        if (!groupsRes.ok) {
            setError('Error loading user groups. Error status: ' + groupsRes.status + " OK: " + groupsRes.ok);
            return;
        }

        const groupObjects = (groupsRes.data ?? []).map((g) => ({
          id: g._id,
          name: g.name
        })).filter(Boolean);

        setGroups(groupObjects);

        // sensible defaults
        setFormState((prev) => {
          const next = { ...prev };
          if (!next.selectedGroups.length && groupObjects.length) next.selectedGroups = [groupObjects[0].name];
          return next;
        });
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Failed to load folders/groups");
      } finally {
        setLoadingInit(false);
      }
    }

    loadInitial();
    loadX3Folders()
    return () => controller.abort();
  }, []);

  // ---------- Load users when folder changes ----------
  useEffect(() => {
    if (!formState.folder) return;

    const controller = new AbortController();

    async function loadUsers() {
      setError("");
      setSuccessMsg("");
      setWarningMsg("");
      setLoadingUsers(true);
      setX3Users([]);

      // reset folder-specific selections when folder changes
      setFormState((prev) => ({
        ...prev,
        selectedUsrSet: new Set(),
        userSearch: "",
      }));

      try {
        if (!formState.folder) {
          setError('Error loading x3 users. No folders found.');
          return;
        }
        const result = await getX3Users(formState.folder);
        if (!result.ok) {
            setError('Error loading x3 users. Error status: ' + result.status + " OK: " + result.ok);
            return;
        }

        setX3Users(Array.isArray(result.users) ? result.users : []);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Failed to load users");
      } finally {
        setLoadingUsers(false);
      }
    }

    loadUsers();
    return () => controller.abort();
  }, [formState.folder]);

  // ---------- View-side handlers ----------
  const updateField = (patch) => {
    setFormState((prev) => ({ ...prev, ...patch }));
  };

  const toggleUser = (usrKey) => {
    if (!usrKey) return;

    setFormState((prev) => {
      const next = new Set(prev.selectedUsrSet);
      if (next.has(usrKey)) next.delete(usrKey);
      else next.add(usrKey);
      return { ...prev, selectedUsrSet: next };
    });
  };

  const toggleSelectAll = () => {
    setFormState((prev) => {
      if (!x3Users.length) return prev;

      const allKeys = x3Users.map((u) => u.USR).filter(Boolean);
      const isAllSelected = prev.selectedUsrSet.size === allKeys.length;

      return {
        ...prev,
        selectedUsrSet: isAllSelected ? new Set() : new Set(allKeys),
      };
    });
  };

  const validate = () => {
    if (!formState.folder) return "Choose a folder first.";
    if (!formState.selectedUsrSet.size) return "Select at least one user.";
    if (!formState.selectedGroups.length) return "Select at least one group.";
    return "";
  };

  const handleSubmit = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      setSuccessMsg("");
      setWarningMsg("");
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMsg("");
    setWarningMsg("");

    const selectedUsers = x3Users.filter((u) => formState.selectedUsrSet.has(u.USR));

    const selectedGroupIds = groups
      .filter(group => formState.selectedGroups.includes(group.name))
      .map(group => group.id);

    const payload = {
      users: selectedUsers,
      folder: formState.folder,
      groups: selectedGroupIds,
      allFolders: formState.setAllFolders,
    };

    try {
      const result = await importX3Users(payload);

      const data = result.data;

      if (result.status === 201) {
        setSuccessMsg(`Imported ${selectedUsers.length} user(s) from ${formState.folder}.`);
        setFormState((prev) => ({ ...prev, selectedUsrSet: new Set() })); // clearing selection. optional
      }
      else if (result.status === 207) {
        setWarningMsg(`Imported ${data.created}, skipped ${data.skipped}, failed ${data.failed.length}.`);
        setFormState((prev) => ({ ...prev, selectedUsrSet: new Set() }));
      }
      else if (result.status === 409) {
        setWarningMsg(`No new users imported. Skipped ${data.skipped}, failed ${data.failed.length}.`);
      }
      else {
        setError(data.error || "Import failed");
      }

      window.scrollTo({ top: 0, behavior: "smooth" });     

    } catch (e) {
      setError(e.message || "Failed to add users");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="">
        <Header visible={!props.parsed.X3} />
      <div className="page-wrapper">
        <div className="s-page-header">
        
          <Stack spacing={0.5} alignItems="flex-start" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Import Users
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select a folder, pick users, assign groups, then import. Users will be prompted to set their own password on first login.
            </Typography>
          </Stack>
          <Button
          variant='text'
          type="button"
          onClick={handleGoBackClick}
          startIcon={<ArrowBackIcon />}
          sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
              border:'1px solid grey'
          }}
          >
            Back to Users
        </Button>
        </div>
        

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}
        {warningMsg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warningMsg}
          </Alert>
        )}

        {isAdmin ? (
          <Paper elevation={1} sx={{ p: 2, borderTop: '4px solid #7443f4' }}>
            <ImportX3UsersForm
              // data
              folders={folders}
              groups={groups}
              users={x3Users}
              // state
              value={formState}
              // ui state
              loadingInit={loadingInit}
              loadingUsers={loadingUsers}
              saving={saving}
              // labels
              folderLabel={labels.folderLabel}
              addUsersLabel={labels.addUsersLabel}
              // events
              onChange={updateField}
              onToggleUser={toggleUser}
              onToggleSelectAll={toggleSelectAll}
              onX3FolderRefresh={loadX3Folders}
              onSubmit={handleSubmit}
            />
          </Paper>
        ) : (
          <Alert severity="error" sx={{ mt: 2 }}>
            You do not have permission to access this page. Redirecting...
          </Alert>
        )}
      </div>
    </div>
  );
}
export default connect(function (state) { return state; })(
  AuthHOC(ImportUsersView, { name: 'administration' })
);